import { useState } from "react";
import { useAuth } from "react-oidc-context";
import { ClientsPage } from "./pages/Clients";
import { UsersPage } from "./pages/Users";
import { displayName, groupsOf, hasAnyRole, isAdmin } from "./lib/auth";
import { hostedLogoutUrl, nonProductionLabel } from "./lib/config";

/** Two pages, so this is a tab rather than a router.
 *
 *  Adding react-router would also mean deciding what /staff does on a hard
 *  refresh, which currently works only because CloudFront rewrites unknown
 *  paths to index.html for the OIDC callback. Not worth it for one more page. */
type Tab = "clients" | "staff";

export function App() {
  const auth = useAuth();

  if (auth.isLoading) {
    return <Shell><p className="muted">Signing in…</p></Shell>;
  }

  if (auth.error) {
    return (
      <Shell>
        <div className="notice error">
          <strong>Could not sign in.</strong>
          <p>{auth.error.message}</p>
          <button onClick={() => void auth.signinRedirect()}>Try again</button>
        </div>
      </Shell>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <Shell>
        <div className="signin">
          <p>Sign in with your Casa Advisory account.</p>
          <button className="primary" onClick={() => void auth.signinRedirect()}>
            Sign in
          </button>
        </div>
      </Shell>
    );
  }

  // An account with no group can reach nothing. Saying so beats an empty page
  // followed by a 403 from every request.
  if (!hasAnyRole(auth.user)) {
    return (
      <Shell user={displayName(auth.user)}>
        <div className="notice error">
          <strong>No role assigned.</strong>
          <p>
            Your account exists but has no role yet. An administrator needs to add
            you to Preparer, Reviewer, Director Approval or Admin.
          </p>
        </div>
      </Shell>
    );
  }

  return <SignedIn />;
}

function SignedIn() {
  const auth = useAuth();
  const [tab, setTab] = useState<Tab>("clients");
  const admin = isAdmin(auth.user);

  return (
    <Shell
      user={displayName(auth.user)}
      roles={groupsOf(auth.user)}
      // Staff management is Admin-only and enforced server-side; hiding the
      // tab is presentation, not the control.
      tabs={admin ? { current: tab, onChange: setTab } : undefined}
    >
      {admin && tab === "staff" ? <UsersPage /> : <ClientsPage />}
    </Shell>
  );
}

function Shell(props: {
  children: React.ReactNode;
  user?: string;
  roles?: string[];
  tabs?: { current: Tab; onChange: (tab: Tab) => void };
}) {
  const environment = nonProductionLabel();
  return (
    <>
      {environment && (
        <div className="env-bar">
          {environment} environment &mdash; holds real client data, but is not
          the production system
        </div>
      )}
      <header>
        <div>
          <h1>
            Casa Advisory
            {environment && <span className="env">{environment}</span>}
          </h1>
          <p className="muted">BAS Workpapers</p>
        </div>
        {props.user && (
          <div className="session">
            <span>{props.user}</span>
            {props.roles && props.roles.length > 0 && (
              <span className="roles">{props.roles.join(" · ")}</span>
            )}
            <a href={hostedLogoutUrl()}>Sign out</a>
          </div>
        )}
      </header>
      {props.tabs && (
        <nav className="tabs">
          {([
            ["clients", "Clients"],
            ["staff", "Staff"],
          ] as const).map(([value, text]) => (
            <button
              key={value}
              className={props.tabs?.current === value ? "tab current" : "tab"}
              aria-current={props.tabs?.current === value ? "page" : undefined}
              onClick={() => props.tabs?.onChange(value)}
            >
              {text}
            </button>
          ))}
        </nav>
      )}
      <main>{props.children}</main>
    </>
  );
}
